import { Skeleton } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import type { DashboardSummary } from '@/services/dashboard.service';
import type { ReactNode } from 'react';

interface DashboardKpiRowProps {
  data: DashboardSummary;
  loading?: boolean;
  /** Click handlers for each KPI. Cards are clickable iff a handler is given. */
  onApprovalsClick?: () => void;
  onRevenueClick?: () => void;
  onBillsClick?: () => void;
  onAvgClick?: () => void;
  onProfitClick?: () => void;
}

interface KpiCardConfig {
  title: string;
  value: string;
  icon: ReactNode;
  gradient: string;
  iconBg: string;
  borderAccent: string;
  tintBg: string;
  darkTintBg: string;
  trend?: { direction: 'up' | 'down' | 'flat'; value: string };
  anomaly?: boolean;
  onClick?: () => void;
}

function trendData(current: number, previous: number) {
  if (previous === 0) return undefined;
  const pct = Math.round(((current - previous) / previous) * 100);
  return {
    direction: (pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat') as 'up' | 'down' | 'flat',
    value: `${Math.abs(pct)}%`,
  };
}

function TrendPill({ trend }: { trend: { direction: 'up' | 'down' | 'flat'; value: string } }) {
  const isPositive = trend.direction === 'up';
  const isNeutral = trend.direction === 'flat';

  const colorClass = isNeutral
    ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
    : isPositive
      ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
      : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400';

  const arrow = trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→';

  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
      {arrow} {trend.value}
    </span>
  );
}

function KpiCard({ title, value, icon, gradient, iconBg, borderAccent, tintBg, darkTintBg, trend, anomaly, onClick }: KpiCardConfig) {
  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      className={`relative overflow-hidden rounded-xl ${tintBg} ${darkTintBg} border ${borderAccent} shadow-md hover:shadow-lg transition-all duration-200 p-5 group ${onClick ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500' : ''} ${anomaly ? 'ring-2 ring-danger-500 animate-pulse-soft' : ''}`}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
    >
      {/* Decorative gradient blob */}
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full ${gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />

      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center text-white shadow-sm`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <div className="relative">
        <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
        {trend && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <TrendPill trend={trend} />
            <span className="text-xs text-neutral-400 dark:text-neutral-500">vs yesterday</span>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

export function DashboardKpiRow({
  data,
  loading,
  onApprovalsClick,
  onRevenueClick,
  onBillsClick,
  onAvgClick,
  onProfitClick,
}: DashboardKpiRowProps) {
  const currency = useTenantCurrency();
  // QA 8.7.11 — the server applies the rule (today so far vs the same weekday's
  // average for the same period); the card ring and the alert follow it.
  const anomaly = data.anomaly;
  const isAnomaly = anomaly?.is_anomaly === true;

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={120} />
        ))}
      </div>
    );
  }

  // Cards are built then filtered — slots like Pending Approvals are noise
  // when zero. Filtering keeps the grid compact and lets vertical-specific
  // cards (Gross Profit, New vs Returning) take their place when relevant.
  const allCards: (KpiCardConfig & { showWhen?: boolean })[] = [
    {
      title: "Today's Revenue",
      value: formatPaise(data.today.revenue, currency),
      gradient: 'bg-gradient-to-br from-brand-500 to-indigo-500',
      iconBg: 'bg-gradient-to-br from-brand-500 to-indigo-500',
      borderAccent: 'border-brand-200 dark:border-brand-800',
      tintBg: 'bg-brand-50/60',
      darkTintBg: 'dark:bg-brand-950/30',
      trend: trendData(data.today.revenue, data.yesterday.revenue),
      anomaly: isAnomaly,
      onClick: onRevenueClick,
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Bills Today',
      value: String(data.today.bill_count),
      gradient: 'bg-gradient-to-br from-violet-500 to-pink-500',
      iconBg: 'bg-gradient-to-br from-violet-500 to-pink-500',
      borderAccent: 'border-violet-200 dark:border-violet-800',
      tintBg: 'bg-violet-50/60',
      darkTintBg: 'dark:bg-violet-950/30',
      trend: trendData(data.today.bill_count, data.yesterday.bill_count),
      onClick: onBillsClick,
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      title: 'Avg Bill Value',
      value: formatPaise(data.today.avg_bill_value, currency),
      gradient: 'bg-gradient-to-br from-teal-500 to-emerald-500',
      iconBg: 'bg-gradient-to-br from-teal-500 to-emerald-500',
      borderAccent: 'border-teal-200 dark:border-teal-800',
      tintBg: 'bg-teal-50/60',
      darkTintBg: 'dark:bg-teal-950/30',
      trend: trendData(data.today.avg_bill_value, data.yesterday.avg_bill_value),
      onClick: onAvgClick,
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      title: 'Gross Profit',
      // Only show when we have actual cost data; if every product has
      // unit_cost = 0 the margin is misleadingly 100%.
      showWhen: (data.today.gross_profit ?? 0) > 0,
      value: formatPaise(data.today.gross_profit ?? 0, currency),
      gradient: 'bg-gradient-to-br from-success-500 to-emerald-500',
      iconBg: 'bg-gradient-to-br from-success-500 to-emerald-500',
      borderAccent: 'border-success-200 dark:border-success-800',
      tintBg: 'bg-success-50/60',
      darkTintBg: 'dark:bg-success-950/30',
      trend: data.today.margin_pct !== undefined
        ? {
            direction: 'flat' as const,
            value: `${data.today.margin_pct}% margin`,
          }
        : undefined,
      onClick: onProfitClick,
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
    },
    {
      title: 'Pending Approvals',
      // Hide when there's nothing to approve — a "0" pending card is
      // permanent dead space on most days.
      showWhen: data.pending_approvals > 0,
      value: String(data.pending_approvals),
      gradient: 'bg-gradient-to-br from-amber-500 to-orange-500',
      iconBg: 'bg-gradient-to-br from-amber-500 to-orange-500',
      borderAccent: 'border-amber-200 dark:border-amber-800',
      tintBg: 'bg-amber-50/60',
      darkTintBg: 'dark:bg-amber-950/30',
      onClick: onApprovalsClick,
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  const cards = allCards.filter((c) => c.showWhen !== false);

  // Responsive grid that adapts to the number of visible cards. Up to 6
  // columns on xl so vertical-specific cards (Gross Profit, New vs
  // Returning) can fit without crushing the row.
  const cols =
    cards.length <= 2
      ? 'grid-cols-2'
      : cards.length === 3
      ? 'grid-cols-2 md:grid-cols-3'
      : cards.length === 4
      ? 'grid-cols-2 md:grid-cols-4'
      : cards.length === 5
      ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'
      : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6';

  return (
    <>
      {isAnomaly && anomaly && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-3 rounded-xl border border-danger-200 dark:border-danger-800 bg-gradient-to-r from-danger-50 to-warning-50 dark:from-danger-950/40 dark:to-warning-950/30 p-4 shadow-md animate-fade-in"
        >
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-danger-500 to-warning-500 text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
            </svg>
          </span>
          <div className="text-sm">
            <p className="font-semibold text-danger-700 dark:text-danger-300">
              Sales are {Math.abs(Math.round(anomaly.change_pct ?? 0))}% below the usual for this day
            </p>
            <p className="text-neutral-600 dark:text-neutral-300">
              {formatPaise(data.today.revenue, currency)} so far against an average of{' '}
              {formatPaise(anomaly.weekday_average, currency)} by {anomaly.as_of.slice(0, 5)} on the
              last {anomaly.weeks_compared} same weekdays.
            </p>
          </div>
        </div>
      )}
      <div className={`grid ${cols} gap-4 lg:gap-6`}>
        {cards.map((card) => (
          <KpiCard key={card.title} {...card} />
        ))}
      </div>
    </>
  );
}
